"use client";

import { logger } from "@utilities/logger";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useRef } from "react";

type AnalyticsBrowserInstance = {
  page: () => void;
  track: (event: string) => void;
  identify: (id: string | null, traits: { email: string }) => void;
};

// Initialize analytics only once using a singleton pattern
let dreamdata: AnalyticsBrowserInstance | null = null;

async function initializeAnalytics(): Promise<void> {
  if (dreamdata) {
    return;
  }

  try {
    const { AnalyticsBrowser } = await import("@dreamdata/analytics-next");
    dreamdata = AnalyticsBrowser.load({
      writeKey: "01698830-90d1-4e1f-aea3-aea9340cf7b3",
    });
  } catch (error) {
    logger.warn("Failed to initialize analytics:", error);
  }
}

function trackPageView(context: string): void {
  if (dreamdata) {
    try {
      dreamdata.page();
    } catch (error) {
      logger.warn(`Failed to track page view (${context}):`, error);
    }
  }
}

export function trackContactSalesFormDreamdata(email: string): void {
  if (dreamdata) {
    try {
      dreamdata.track("contact_sales_form");
      dreamdata.identify(null, { email });
    } catch (error) {
      logger.warn(`Failed to track contact sales form (${email}):`, error);
    }
  }
}

export default function AnalyticsComponent() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isInitialized = useRef(false);
  const lastPathname = useRef(pathname);
  const lastSearchParams = useRef(searchParams.toString());

  useEffect(() => {
    void (async () => {
      await initializeAnalytics();
      isInitialized.current = true;

      // Track the initial page view after initialization
      trackPageView("initial");
    })();
  }, []);

  useEffect(() => {
    // Only track page changes after initialization (not the initial load)
    if (isInitialized.current && dreamdata) {
      const currentSearchParams = searchParams.toString();

      // Check if path or search params actually changed
      if (lastPathname.current !== pathname || lastSearchParams.current !== currentSearchParams) {
        lastPathname.current = pathname;
        lastSearchParams.current = currentSearchParams;

        trackPageView("navigation");
      }
    }
  }, [pathname, searchParams]);

  // Return null since this is just for tracking
  return null;
}
